'use client';

import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { ConcurrentQueue } from './concurrent-queue';
import { type UploadResult, uploadFileInternal } from './upload-internal';

const MAX_CONCURRENT_UPLOADS = 2; // 固定并发数量

// 全局上传队列实例
const uploadQueue = new ConcurrentQueue<UploadResult>(MAX_CONCURRENT_UPLOADS);

export interface UploadController {
  uploadFile: (params: {
    file: Blob;
    width?: number;
    height?: number;
    scene?: UploadScene;
    duration?: number;
    autoPart?: boolean;
    videoAccept?: string[];
  }) => Promise<UploadResult>;
  cancelUpload: () => void;
}

/**
 * 创建上传控制器
 */
export const createUploadController = (): UploadController => {
  const abortController = new AbortController();

  const cancelUpload = () => {
    abortController.abort();
  };

  const uploadFileWithSignal = ({
    file,
    width = 0,
    height = 0,
    scene = UPLOAD_SCENE.VIDU,
    duration,
    autoPart = false,
    videoAccept,
  }: {
    file: Blob;
    width?: number;
    height?: number;
    duration?: number;
    scene?: UploadScene;
    autoPart?: boolean;
    videoAccept?: string[];
  }) => {
    return uploadFile({ file, width, height, scene, abortController, duration, autoPart, videoAccept });
  };
  return { cancelUpload, uploadFile: uploadFileWithSignal };
};

/**
 * 上传文件，支持大文件分块上传，支持传入 AbortSignal 取消请求
 * 通过队列管理器限制并发上传数量
 *
 * @param file 文件
 * @param width 宽度
 * @param height 高度
 * @param scene 场景
 * @param signal 取消请求
 * @returns 上传结果
 */
export const uploadFile = async ({
  file,
  width = 0,
  height = 0,
  scene = UPLOAD_SCENE.VIDU,
  abortController,
  duration,
  videoAccept,
  autoPart = false,
}: {
  file: Blob;
  width?: number;
  height?: number;
  scene?: UploadScene;
  abortController?: AbortController;
  duration?: number;
  videoAccept?: string[];
  /** 是否自动分片上传 */
  autoPart?: boolean;
}): Promise<UploadResult> => {
  return uploadQueue.add(() =>
    uploadFileInternal({ file, width, height, scene, abortController, duration, autoPart, videoAccept }),
  );
};

export type { UploadResult };
